// Pieces of the order review screen shared by grocery and food (canvas: "Cart review").
import type { ComponentChildren } from 'preact';
import { Thumb } from './Thumb';
import { money } from '../commerce';
import { useT } from '../i18n';
import type { Money, Total } from '../types';

export interface ReviewLine { key: string; title: string; detail?: string; qty: number; price: Money; image?: string }

/** Lines with images, then the provider's own price breakdown in its order. */
export function CartSummary({ lines, totals, eta }: { lines: ReviewLine[]; totals: Total[]; eta?: string }) {
  const t = useT();
  return (
    <section class="rows">
      {lines.map((l) => (
        <div class="row" style="min-height:72px">
          <Thumb src={l.image} name={l.title} size="row" />
          <span class="grow" style="line-height:1.3">
            <span class="clamp2" style="font-weight:700;font-size:15px">{l.title}</span>
            <span class="sub">{[l.detail, `× ${l.qty}`].filter(Boolean).join(' · ')}</span>
          </span>
          <span style="flex:none;font-weight:800;font-size:15px">{money(l.price)}</span>
        </div>
      ))}
      <div style="padding:12px 14px;display:flex;flex-direction:column;gap:6px;background:var(--ground)">
        {totals.map((x) => (
          <div style={`display:flex;justify-content:space-between;gap:10px;${x.kind === 'total' ? 'font-weight:800;font-size:18px;padding-top:6px;border-top:1px solid var(--line)' : 'font-size:14px;color:var(--muted)'}`}>
            <span>{x.label}</span><span>{money(x.amount)}</span>
          </div>
        ))}
        {eta && <span class="small muted">{t('food.eta', { eta })}</span>}
      </div>
    </section>
  );
}

export function AddressCard({ label, line }: { label?: string; line: string }) {
  const t = useT();
  return (
    <section class="card" style="min-height:56px;padding:10px 14px;display:flex;align-items:center;gap:10px">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-text)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7-5.6-7-11a7 7 0 0 1 14 0c0 5.4-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
      <span style="flex:1;display:flex;flex-direction:column;line-height:1.3">
        <span class="small muted">{t('food.deliverTo')}</span>
        <span style="font-weight:600;font-size:15px">{label ? `${label} · ` : ''}{line}</span>
      </span>
    </section>
  );
}

/** The fixed bottom of the review: place the order here, or (when the app shares its cart) finish there. */
export function ReviewActions({ place, appURL, appName, note }: { place: ComponentChildren; appURL?: string; appName: string; note?: string }) {
  const t = useT();
  return (
    <div class="bottom-bar">
      <div class="bottom-bar-inner" style="flex-direction:column;align-items:stretch;gap:10px">
        {place}
        {appURL && (
          <>
            <a class="btn btn-outline" style="min-height:50px" href={appURL} target="_blank" rel="noopener">
              {t('order.finishInApp', { app: appName })}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8" /></svg>
            </a>
            <span class="small muted" style="text-align:center">{note || t('order.finishInAppNote', { app: appName })}</span>
          </>
        )}
      </div>
    </div>
  );
}
